/**
 * Diverse categorical palette for suite / category tagging.
 *
 * Hand-picked hues spaced across the colour wheel using OKLCH. Deliberately
 * AVOIDS :
 *   - pure red / pure green / pure orange — reserved for status semantics
 *     (danger, success, warning). Using them as arbitrary tags creates
 *     cognitive noise with run state.
 *
 * Each entry comes in three forms :
 *   - fg   : strong colour for text on the token's soft background
 *   - bg   : soft transparent background (≈ 14 % opacity)
 *   - line : 2 px accent line / left border
 *
 * All values are expressed as OKLCH so they map consistently on all four
 * themes (light + dark).
 */
export interface SuiteColor {
  key: string;
  name: string;
  fg: string;
  bg: string;
  line: string;
}

export const SUITE_PALETTE: SuiteColor[] = [
  // Bleus / cyans
  { key: 'cobalt',    name: 'Cobalt',     fg: 'oklch(0.62 0.17 258)', bg: 'oklch(0.62 0.17 258 / 0.14)', line: 'oklch(0.62 0.17 258)' },
  { key: 'sky',       name: 'Ciel',       fg: 'oklch(0.72 0.12 225)', bg: 'oklch(0.72 0.12 225 / 0.14)', line: 'oklch(0.72 0.12 225)' },
  { key: 'azure',     name: 'Azur',       fg: 'oklch(0.62 0.16 240)', bg: 'oklch(0.62 0.16 240 / 0.14)', line: 'oklch(0.62 0.16 240)' },
  { key: 'denim',     name: 'Denim',      fg: 'oklch(0.50 0.14 250)', bg: 'oklch(0.50 0.14 250 / 0.14)', line: 'oklch(0.50 0.14 250)' },
  { key: 'teal',      name: 'Turquoise',  fg: 'oklch(0.70 0.12 195)', bg: 'oklch(0.70 0.12 195 / 0.14)', line: 'oklch(0.70 0.12 195)' },
  { key: 'mint',      name: 'Menthe',     fg: 'oklch(0.74 0.10 180)', bg: 'oklch(0.74 0.10 180 / 0.14)', line: 'oklch(0.74 0.10 180)' },

  // Violets / indigos
  { key: 'indigo',    name: 'Indigo',     fg: 'oklch(0.55 0.18 275)', bg: 'oklch(0.55 0.18 275 / 0.14)', line: 'oklch(0.55 0.18 275)' },
  { key: 'lavender',  name: 'Lavande',    fg: 'oklch(0.72 0.09 290)', bg: 'oklch(0.72 0.09 290 / 0.14)', line: 'oklch(0.72 0.09 290)' },
  { key: 'violet',    name: 'Violet',     fg: 'oklch(0.65 0.16 300)', bg: 'oklch(0.65 0.16 300 / 0.14)', line: 'oklch(0.65 0.16 300)' },
  { key: 'plum',      name: 'Prune',      fg: 'oklch(0.52 0.14 315)', bg: 'oklch(0.52 0.14 315 / 0.14)', line: 'oklch(0.52 0.14 315)' },

  // Roses / fuchsias
  { key: 'fuchsia',   name: 'Fuchsia',    fg: 'oklch(0.66 0.20 330)', bg: 'oklch(0.66 0.20 330 / 0.14)', line: 'oklch(0.66 0.20 330)' },
  { key: 'rose',      name: 'Rose',       fg: 'oklch(0.68 0.16 345)', bg: 'oklch(0.68 0.16 345 / 0.14)', line: 'oklch(0.68 0.16 345)' },
  { key: 'blush',     name: 'Corail doux',fg: 'oklch(0.74 0.11 10)',  bg: 'oklch(0.74 0.11 10 / 0.14)',  line: 'oklch(0.74 0.11 10)' },

  // Bruns / ocres / ambrés
  { key: 'mustard',   name: 'Moutarde',   fg: 'oklch(0.72 0.14 80)',  bg: 'oklch(0.72 0.14 80 / 0.14)',  line: 'oklch(0.72 0.14 80)' },
  { key: 'amber',     name: 'Ambre',      fg: 'oklch(0.76 0.13 65)',  bg: 'oklch(0.76 0.13 65 / 0.14)',  line: 'oklch(0.76 0.13 65)' },
  { key: 'sand',      name: 'Sable',      fg: 'oklch(0.70 0.08 75)',  bg: 'oklch(0.70 0.08 75 / 0.14)',  line: 'oklch(0.70 0.08 75)' },
  { key: 'bronze',    name: 'Bronze',     fg: 'oklch(0.55 0.11 60)',  bg: 'oklch(0.55 0.11 60 / 0.14)',  line: 'oklch(0.55 0.11 60)' },

  // Verts doux / olives (pas vert pur — réservé au statut)
  { key: 'olive',     name: 'Olive',      fg: 'oklch(0.68 0.10 110)', bg: 'oklch(0.68 0.10 110 / 0.14)', line: 'oklch(0.68 0.10 110)' },
  { key: 'moss',      name: 'Mousse',     fg: 'oklch(0.55 0.09 135)', bg: 'oklch(0.55 0.09 135 / 0.14)', line: 'oklch(0.55 0.09 135)' },

  // Neutres chauds / froids (pour suites techniques, moins saturés)
  { key: 'slate',     name: 'Ardoise',    fg: 'oklch(0.58 0.05 255)', bg: 'oklch(0.58 0.05 255 / 0.14)', line: 'oklch(0.58 0.05 255)' },
  { key: 'graphite',  name: 'Graphite',   fg: 'oklch(0.52 0.03 270)', bg: 'oklch(0.52 0.03 270 / 0.14)', line: 'oklch(0.52 0.03 270)' },
  { key: 'sepia',     name: 'Sépia',      fg: 'oklch(0.54 0.06 60)',  bg: 'oklch(0.54 0.06 60 / 0.14)',  line: 'oklch(0.54 0.06 60)' },
  { key: 'charcoal',  name: 'Charbon',    fg: 'oklch(0.48 0.04 280)', bg: 'oklch(0.48 0.04 280 / 0.14)', line: 'oklch(0.48 0.04 280)' },

  // 5 extra hues so the single-row palette fills a 960-px modal without wrap
  { key: 'seafoam',   name: 'Écume',      fg: 'oklch(0.72 0.08 165)', bg: 'oklch(0.72 0.08 165 / 0.14)', line: 'oklch(0.72 0.08 165)' },
  { key: 'orchid',    name: 'Orchidée',   fg: 'oklch(0.63 0.15 310)', bg: 'oklch(0.63 0.15 310 / 0.14)', line: 'oklch(0.63 0.15 310)' },
  { key: 'wine',      name: 'Lie-de-vin', fg: 'oklch(0.48 0.16 355)', bg: 'oklch(0.48 0.16 355 / 0.14)', line: 'oklch(0.48 0.16 355)' },
  { key: 'pewter',    name: 'Étain',      fg: 'oklch(0.65 0.03 230)', bg: 'oklch(0.65 0.03 230 / 0.14)', line: 'oklch(0.65 0.03 230)' },
  { key: 'tanzanite', name: 'Tanzanite',  fg: 'oklch(0.44 0.18 270)', bg: 'oklch(0.44 0.18 270 / 0.14)', line: 'oklch(0.44 0.18 270)' },
];

/** Stable mapping from a category/suite name to a palette color (hash-based). */
export function colorForKey(key: string): SuiteColor {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) {
    h = (h * 31 + key.charCodeAt(i)) | 0;
  }
  return SUITE_PALETTE[Math.abs(h) % SUITE_PALETTE.length];
}

/**
 * Dedicated KPI palette — exactly 6 hues picked by hand so that
 * adjacent tiles are at least 40° apart on the OKLCH hue wheel, and
 * all hues avoid the reserved zones (red / green / orange).
 *
 * Hue audit :
 *   sky 220 · sun 80 · aqua 175 · orchid 300 · slate 255 · magenta 340
 *   min(Δh) = 40° between sun 80 and aqua 175's 95° distance, or
 *             40° between orchid 300 and magenta 340.
 *
 * Adjacent tile pairs in the Dashboard grid :
 *   systems 220 → tests 80   : Δ=140° ✓
 *   tests 80   → suites 340  : Δ=100° ✓
 *   suites 340 → categories 175 : Δ=165° ✓
 *   categories 175 → runs 300   : Δ=125° ✓
 *   runs 300 → passrate 80     : Δ=140° ✓
 */
export const KPI_PALETTE: SuiteColor[] = [
  { key: 'sky',     name: 'Ciel',     fg: 'oklch(0.68 0.14 220)', bg: 'oklch(0.68 0.14 220 / 0.14)', line: 'oklch(0.68 0.14 220)' },
  { key: 'sun',     name: 'Soleil',   fg: 'oklch(0.76 0.14 80)',  bg: 'oklch(0.76 0.14 80 / 0.14)',  line: 'oklch(0.76 0.14 80)'  },
  { key: 'aqua',    name: 'Aqua',     fg: 'oklch(0.72 0.12 175)', bg: 'oklch(0.72 0.12 175 / 0.14)', line: 'oklch(0.72 0.12 175)' },
  { key: 'orchid',  name: 'Orchidée', fg: 'oklch(0.65 0.16 300)', bg: 'oklch(0.65 0.16 300 / 0.14)', line: 'oklch(0.65 0.16 300)' },
  { key: 'slate',   name: 'Ardoise',  fg: 'oklch(0.60 0.17 255)', bg: 'oklch(0.60 0.17 255 / 0.14)', line: 'oklch(0.60 0.17 255)' },
  { key: 'magenta', name: 'Magenta',  fg: 'oklch(0.66 0.20 340)', bg: 'oklch(0.66 0.20 340 / 0.14)', line: 'oklch(0.66 0.20 340)' },
];
